import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, MapPin, Mail, Clock } from "lucide-react";
import { Reveal } from "../lib/motion";
import { LogoMark } from "../components/LogoMark";
import Seo from "../components/Seo";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder:text-white/35 focus:outline-none focus:border-steel-light/60 focus:bg-white/[0.05] transition-all";

const PROPERTY_TYPES = ["Residential Home", "Office", "Medical Facility", "Retail Space", "Industrial Property", "Short-Term Rental / Airbnb", "Property Management", "Post-Construction", "Other"];
const SERVICES = ["Residential Cleaning", "Commercial Cleaning", "Office Cleaning", "Move-Out Cleaning", "Post-Construction Cleaning", "Recurring / Janitorial", "Facility Services", "Other"];
const DEPARTMENTS = [
  ["General", "info@northpeakfs.ca"],
  ["Sales", "sales@northpeakfs.ca"],
  ["Quotes", "quotes@northpeakfs.ca"],
  ["Support", "support@northpeakfs.ca"],
  ["Careers", "careers@northpeakfs.ca"],
  ["Operations", "operations@northpeakfs.ca"],
  ["Billing", "billing@northpeakfs.ca"],
];

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", property_address: "", property_type: "", service_requested: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error(t("Please fill in your name, email and message.")); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, subject: form.service_requested || "Quote request" });
      setDone(true);
      toast.success(t("Request sent! We'll respond within one business day."));
    } catch { toast.error(t("Something went wrong. Please try again.")); }
    setLoading(false);
  };

  return (
    <div data-testid="contact-page" className="bg-ink-950">
      <Seo title="Contact" path="/contact"
        description="Contact North Peak Facility Services in Montreal, Quebec. Request a free quote for residential, commercial, office, move-out, or post-construction cleaning across the Greater Montreal Area."
        keywords="Contact Cleaning Company Montreal, Free Cleaning Quote Montreal" />
      <section className="relative pt-40 pb-16 md:pt-56 overflow-hidden glow">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <LogoMark className="h-12 w-12 mb-8" color="#7FA8C9" />
          <p className="overline text-steel-light mb-5">{t("Contact · Montreal, Quebec")}</p>
          <h1 className="font-serif text-gradient text-4xl md:text-6xl lg:text-7xl leading-[1.05] max-w-3xl" style={{ fontWeight: 500 }}>
            {t("Let's Discuss Your Cleaning Needs.")}
          </h1>
          <p className="mt-6 text-white/60 text-lg md:text-xl leading-relaxed max-w-2xl font-300">
            {t("Whether you need residential cleaning, commercial cleaning, post-construction cleaning, or facility services, our team is ready to help.")}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-10">
          <div>
            <Reveal>
              <p className="overline text-steel-light mb-5">{t("Reach Us")}</p>
              <h2 className="font-serif text-gradient text-3xl md:text-4xl mb-8" style={{ fontWeight: 500 }}>{t("Proudly serving the Greater Montreal Area.")}</h2>
            </Reveal>
            <div className="space-y-4">
              <Reveal>
                <div className="glass rounded-2xl p-5 flex gap-4">
                  <div className="h-11 w-11 rounded-xl bg-white/5 flex items-center justify-center shrink-0"><MapPin className="h-5 w-5 text-steel-light" /></div>
                  <div>
                    <div className="text-white/40 text-xs uppercase tracking-wider">{t("Service Area")}</div>
                    <div className="text-white font-500">{t("Montreal & the Greater Montreal Area, Quebec")}</div>
                    <div className="text-white/45 text-sm mt-1">Laval · Longueuil · Brossard · West Island · South Shore · North Shore · Lachine · LaSalle · Verdun · Saint-Laurent · Dorval · Pointe-Claire · Kirkland · Beaconsfield · Mont-Royal · Westmount</div>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="glass rounded-2xl p-5 flex gap-4">
                  <div className="h-11 w-11 rounded-xl bg-white/5 flex items-center justify-center shrink-0"><Clock className="h-5 w-5 text-steel-light" /></div>
                  <div>
                    <div className="text-white/40 text-xs uppercase tracking-wider">{t("Availability")}</div>
                    <div className="text-white font-500">{t("Email us anytime")}</div>
                    <div className="text-white/45 text-sm mt-1">{t("Response within one business day")}</div>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="glass rounded-2xl p-5">
                  <div className="flex gap-4 mb-4">
                    <div className="h-11 w-11 rounded-xl bg-white/5 flex items-center justify-center shrink-0"><Mail className="h-5 w-5 text-steel-light" /></div>
                    <div>
                      <div className="text-white/40 text-xs uppercase tracking-wider">{t("Email")}</div>
                      <a href="mailto:info@northpeakfs.ca" className="text-white font-500 hover:text-steel-light transition-colors">info@northpeakfs.ca</a>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 pl-15">
                    {DEPARTMENTS.slice(1).map(([label, email]) => (
                      <div key={email} className="text-sm">
                        <span className="text-white/40">{t(label)}: </span>
                        <a href={`mailto:${email}`} className="text-white/70 hover:text-steel-light transition-colors break-all">{email}</a>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.3}>
              <div data-testid="google-map" className="mt-6 aspect-[16/10] w-full overflow-hidden rounded-3xl border border-white/10">
                <iframe title="North Peak — Montreal service area" width="100%" height="100%" style={{ border: 0, filter: "grayscale(0.4) invert(0.9) contrast(0.9)" }} loading="lazy"
                  src="https://www.google.com/maps?q=Montreal,Quebec,Canada&output=embed" />
              </div>
            </Reveal>
          </div>

          <div>
            {done ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="contact-success" className="glass rounded-4xl text-center py-16 px-8">
                <div className="h-16 w-16 rounded-3xl bg-steel/20 text-steel-light flex items-center justify-center mx-auto mb-6"><Check className="h-8 w-8" /></div>
                <h2 className="font-serif text-3xl text-white mb-3" style={{ fontWeight: 500 }}>{t("Thank you for contacting North Peak Facility Services.")}</h2>
                <p className="text-white/55">{t("A member of our team will respond within one business day.")}</p>
              </motion.div>
            ) : (
              <div className="glass rounded-4xl p-8 md:p-10">
                <h3 className="font-serif text-2xl text-white mb-8" style={{ fontWeight: 500 }}>{t("Request your free quote")}</h3>
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <input data-testid="contact-name" className={inputCls} placeholder={t("Name *")} value={form.name} onChange={set("name")} />
                  <input data-testid="contact-company" className={inputCls} placeholder={t("Company (optional)")} value={form.company} onChange={set("company")} />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <input data-testid="contact-email" type="email" className={inputCls} placeholder={t("Email *")} value={form.email} onChange={set("email")} />
                    <input data-testid="contact-phone" className={inputCls} placeholder={t("Phone")} value={form.phone} onChange={set("phone")} />
                  </div>
                  <input data-testid="contact-address" className={inputCls} placeholder={t("Property Address")} value={form.property_address} onChange={set("property_address")} />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <select data-testid="contact-property-type" className={inputCls} value={form.property_type} onChange={set("property_type")}>
                      <option value="" className="bg-ink-800">{t("Property Type")}</option>
                      {PROPERTY_TYPES.map((p) => <option key={p} value={p} className="bg-ink-800">{t(p)}</option>)}
                    </select>
                    <select data-testid="contact-service" className={inputCls} value={form.service_requested} onChange={set("service_requested")}>
                      <option value="" className="bg-ink-800">{t("Service Requested")}</option>
                      {SERVICES.map((s) => <option key={s} value={s} className="bg-ink-800">{t(s)}</option>)}
                    </select>
                  </div>
                  <textarea data-testid="contact-message" rows={4} className={inputCls + " resize-none"} placeholder={t("Tell us about your space and needs *")} value={form.message} onChange={set("message")} />
                  <button type="submit" disabled={loading} data-testid="contact-submit"
                    className="btn-pill w-full bg-white text-ink-950 py-4 text-sm font-600 hover:shadow-xl hover:shadow-white/20 disabled:opacity-50">
                    {loading ? t("Sending…") : t("Request My Free Quote")}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
